import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { toast } from '@heroui/react';
import { ConfigData, Route } from '@/types';
import { useConfigSettings } from '@/hooks/useConfigSettings';
import { DEFAULT_CONFIG } from '@lib/defaultConfig';
import { buildCreateModeConfig, cloneConfigBase } from '@lib/createModeConfig';
import { normalizeConfigUrlForInteractivity } from '@lib/normalizeConfigUrlForInteractivity';
import { useSession } from '@context/sessionProvider';
import { useRouter } from 'next/router';
import { applyTimerTemplate } from '@lib/timerTemplates';

interface SettingsContextProps {
	settings: ConfigData;
	localConfigData: ConfigData;
	configId: number | undefined;
	updateSettings: (updatedConfig: Partial<ConfigData>) => void;
	saveSettings: () => Promise<void>;
	isLoading: boolean;
	isEditMode: boolean;
}

interface SettingsProviderProps {
	tid: string | undefined;
	children: ReactNode;
	onLoad?: () => void;
	onError?: (error: string) => void;
}

export const SettingsContext = createContext<SettingsContextProps | undefined>(undefined);

export function useSettings(): SettingsContextProps {
	const context = useContext(SettingsContext);
	if (!context) {
		throw new Error('useSettings must be used within a SettingsProvider');
	}

	return context;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ tid, children, onLoad, onError }) => {
	const { user } = useSession();
	const router = useRouter();
	const [localConfigData, setLocalConfigData] = useState<ConfigData>(() => cloneConfigBase());
	const initializedTemplateKeyRef = useRef<string | null>(null);
	const dirtyCreateTemplateKeyRef = useRef<string | null>(null);

	const [isSaving, setIsSaving] = useState(false);
	const [activeTid, setActiveTid] = useState<number | undefined>(() =>
		tid && !Number.isNaN(Number(tid)) ? Number.parseInt(tid, 10) : undefined,
	);

	const mode = activeTid ? Route.EDIT : Route.CREATE;
	const isEditMode = mode === Route.EDIT;
	const isCreateMode = !isEditMode;
	const isCreatePage = router.isReady && router.pathname === '/create';
	const templateId = getQueryValue(router.query.template);
	const countdownPosition = getQueryValue(router.query.countdownPosition);
	const createTemplateKey = `${templateId ?? 'standard'}:${countdownPosition ?? 'RIGHT_OF_TITLE'}:${user.timezoneName ?? ''}`;

	const { configData, isLoading, error, saveSettings: saveSettingsToServer } = useConfigSettings(activeTid);

	useEffect(() => {
		const parsedTid = tid && !Number.isNaN(Number(tid)) ? Number.parseInt(tid, 10) : undefined;
		setActiveTid(parsedTid);
	}, [tid]);

	useEffect(() => {
		if (!isCreatePage) {
			initializedTemplateKeyRef.current = null;
			dirtyCreateTemplateKeyRef.current = null;
			return;
		}

		if (
			!isCreateMode ||
			dirtyCreateTemplateKeyRef.current === createTemplateKey ||
			initializedTemplateKeyRef.current === createTemplateKey
		) {
			return;
		}

		const createConfig = buildCreateModeConfig(user.timezoneName);
		setLocalConfigData(applyTimerTemplate(createConfig, templateId, countdownPosition));
		initializedTemplateKeyRef.current = createTemplateKey;
		dirtyCreateTemplateKeyRef.current = null;
	}, [isCreatePage, isCreateMode, createTemplateKey, templateId, countdownPosition, user.timezoneName]);

	useEffect(() => {
		if (isEditMode && configData) {
			setLocalConfigData(configData);
		}
	}, [isEditMode, configData]);

	useEffect(() => {
		if (isEditMode && error && onError) {
			onError('Failed to load settings. Please try again.');
		}
	}, [onError, isEditMode, error]);

	useEffect(() => {
		if (!onLoad) {
			return;
		}

		if (isCreateMode) {
			onLoad();

			return;
		}

		if (isEditMode && !isLoading) {
			if (error) {
				return;
			}
			onLoad();
		}
	}, [onLoad, isCreateMode, isEditMode, isLoading, error]);

	const getChangedFields = (original: ConfigData, updated: ConfigData): Partial<ConfigData> => {
		return Object.fromEntries(
			Object.entries(updated).filter(([key, value]) => original[key as keyof ConfigData] !== value),
		) as Partial<ConfigData>;
	};

	const updateSettings = (updatedConfig: Partial<ConfigData>) => {
		if (isCreatePage && isCreateMode && initializedTemplateKeyRef.current) {
			dirtyCreateTemplateKeyRef.current = initializedTemplateKeyRef.current;
		}

		setLocalConfigData((prev) => {
			const filteredConfig = Object.fromEntries(
				Object.entries(updatedConfig).filter(([, value]) => value !== undefined),
			) as Partial<ConfigData>;

			return { ...prev, ...filteredConfig };
		});
	};

	const saveSettings = async () => {
		setIsSaving(true);
		try {
			if (isEditMode) {
				if (!activeTid) {
					throw new Error('Timer ID is missing.');
				}

				let updatedConfig = configData ? getChangedFields(configData, localConfigData) : localConfigData;
				updatedConfig = normalizeConfigUrlForInteractivity(updatedConfig, localConfigData.interactivity);
				if (Object.keys(updatedConfig).length === 0) {
					toast.success('Settings', { description: 'Settings saved successfully!' });

					return;
				}

				await saveSettingsToServer(updatedConfig, activeTid);

				return;
			}

            const createPayload = normalizeConfigUrlForInteractivity(localConfigData, localConfigData.interactivity);
            const { id } = await saveSettingsToServer(createPayload);
            if (id) {
                setActiveTid(id);
                await router.replace({ pathname: '/edit/[tid]', query: { tid: id } });
            }
        } catch (saveError) {
            toast.danger('Settings', {
                description: (saveError as Error).message ?? 'An unexpected error occurred.',
            });
        } finally {
            setIsSaving(false);
        }
    };

	const settings = isEditMode && configData ? configData : DEFAULT_CONFIG;

	return (
		<SettingsContext.Provider
			value={{
				settings,
				localConfigData,
				configId: activeTid,
				updateSettings,
				saveSettings,
				isLoading: isSaving,
				isEditMode,
			}}
		>
			{children}
		</SettingsContext.Provider>
	);
};

function getQueryValue(value: string | string[] | undefined): string | undefined {
	return Array.isArray(value) ? value[0] : value;
}
